import * as React from 'react'
import { useMemo, useState } from 'react'

import Dialog from '@mui/material/Dialog'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import useTheme from '@mui/material/styles/useTheme'
import type { TextFieldProps } from '@mui/material/TextField'

import { Form, Formik } from 'formik'

import { useData, useHandlers, useValidation } from '../hooks'

import { ConditionFields } from './ConditionFields'
import { ConditionsActions } from './ConditionsActions'

import type { ConditionModel, LeadAttribute } from '../../../typings'

type Props = {
  open: boolean
  setOpen: (value: boolean) => void
  save: (value: ConditionModel) => Promise<boolean>
  textFieldProps: TextFieldProps
  editValues?: ConditionModel
  assignmentRuleConditionsSelectedAttributes: LeadAttribute[]
  cancel: () => void
  onDrawNewTerritory?: (condition: ConditionModel) => void
  onRedrawTerritory?: (condition: ConditionModel, territoryId: number) => void
}

export function ConditionFormPopup(props: Props) {
  const {
    open,
    setOpen,
    textFieldProps,
    save,
    editValues,
    assignmentRuleConditionsSelectedAttributes,
    cancel,
    onDrawNewTerritory,
    onRedrawTerritory,
  } = props
  const { handleSave, handleDialogClose } = useHandlers({
    setOpen,
    save,
    cancel,
  })
  const { initValues } = useData()
  const { validate } = useValidation()
  const theme = useTheme()
  const [zipCodeValid, setZipCodeValid] = useState(true)

  const title = useMemo(
    () =>
      editValues && !!Object.keys(editValues).length
        ? 'Edit Condition'
        : 'Add Condition',
    [editValues],
  )

  return (
    <Dialog open={open} onClose={handleDialogClose}>
      <Paper
        variant="outlined"
        elevation={0}
        sx={{ boxShadow: '0px 0px 0px 1px #E0E0E0' }}
      >
        <Stack p={2} width={606}>
          <Typography variant="h6" color={theme.palette.text.primary} mb={1}>
            {title}
          </Typography>

          <Formik
            initialValues={editValues || initValues}
            onSubmit={(values: ConditionModel, { setErrors, setSubmitting }) =>
              handleSave(values, zipCodeValid, setErrors, setSubmitting)
            }
            validate={validate}
            validateOnBlur={false}
          >
            <Form>
              <ConditionFields
                textFieldProps={textFieldProps}
                setZipCodeValid={setZipCodeValid}
                assignmentRuleConditionsSelectedAttributes={
                  assignmentRuleConditionsSelectedAttributes
                }
                onDrawNewTerritory={onDrawNewTerritory}
                onRedrawTerritory={onRedrawTerritory}
              />
              <ConditionsActions cancel={cancel} />
            </Form>
          </Formik>
        </Stack>
      </Paper>
    </Dialog>
  )
}
